import {
  createStyleSheet,
  Divider,
  Focusable,
  Icon,
  Text,
  useStyleSheet,
  useThemeContext,
  View
} from '@gopeerproject/ui-kit'
import { useOutsideAlerter } from '@hooks'
import React, { useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const Dropdown = ({ data, children }) => {
  const modalRoot = useMemo(() => document.getElementById('modal'), [])
  const styles = useStyleSheet(getStyles)
  const { size } = useThemeContext()
  const [isShown, setIsShown] = useState(false)
  const [childrenHeight, setChildrenHeight] = useState(0)
  const [childrenWidth, setChildrenWidth] = useState(0)
  const [childrenTop, setChildrenTop] = useState(0)
  const [childrenLeft, setChildrenLeft] = useState(0)
  const dropdownRef = useRef()
  useOutsideAlerter(dropdownRef, () => setIsShown(false))
  return (
    <>
      <Focusable
        ref={(el) => {
          if (el) {
            const rect = el.getBoundingClientRect()
            setChildrenHeight(rect.height)
            setChildrenWidth(rect.width)
            setChildrenTop(rect.top)
            setChildrenLeft(rect.left)
          }
        }}
        onPress={() => setIsShown(!isShown)}
        onMouseDown={(e) => {
          if (isShown) {
            e.preventDefault()
            e.stopPropagation()
          }
        }}
      >
        {children}
      </Focusable>
      {isShown &&
        createPortal(
          <View
            ref={dropdownRef}
            style={[
              styles.container,
              {
                top: childrenTop + childrenHeight + size.B,
                left: childrenLeft,
                width: childrenWidth
              }
            ]}
          >
            {data.map(({ text, onPress, rightIcon, leftIcon, disabled }, i) => (
              <React.Fragment key={i}>
                <Focusable
                  style={styles.item}
                  hoverStyle={styles.hoverItem}
                  pressStyle={styles.pressedItem}
                  disabledStyle={styles.disabledItem}
                  onPress={(e) => {
                    if (onPress) onPress(e)
                    setIsShown(false)
                  }}
                  disabled={disabled}
                >
                  {leftIcon && <Icon size='16' {...leftIcon} />}
                  <Text
                    type='body'
                    size='md'
                    weight='semibold'
                    style={styles.itemText}
                  >
                    {text}
                  </Text>
                  {rightIcon && <Icon size='16' {...rightIcon} />}
                </Focusable>
                {i !== data.length - 1 && <Divider style={styles.divider} />}
              </React.Fragment>
            ))}
          </View>,
          modalRoot
        )}
    </>
  )
}

const getStyles = createStyleSheet(({ size, color }) => ({
  container: {
    position: 'absolute',
    gap: size.C,
    padding: size.C,
    backgroundColor: color.bg.v3,
    borderColor: color.invert2.p4,
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: size.G,
    alignItems: 'stretch',
    opacity: 1,
    visibility: 'visible',
    zIndex: 999
  },
  hiddenContainer: { opacity: 0, visibility: 'hidden' },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: size.C,
    padding: size.E,
    textAlign: 'left'
  },
  hoverItem: { backgroundColor: color.invert2.p10 },
  pressedItem: { backgroundColor: color.invert2.p10, opacity: 0.7 },
  disabledItem: { opacity: 0.42 },
  itemText: {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    textAlign: 'left',
    flexGrow: 1
  },
  divider: {
    height: 1,
    backgroundColor: color.invert2.p4,
    marginHorizontal: size.E
  }
}))

export default Dropdown
